<template>
    <Teleport to="body">
        <div v-if="emojiPickerStore.isOpen" class="gep-root">
            <div class="gep-backdrop"
                :class="{ 'gep-backdrop--mobile': store.lessThan600 }"
                @click="OnBackdropClick"></div>
            <div ref="popup"
                class="gep-popup"
                :class="{ 'gep-popup--mobile': store.lessThan600 }"
                :style="popupStyle"
                @click.stop>
                <EmojiPicker @select="OnSelect" @close="OnClose" />
            </div>
        </div>
    </Teleport>
</template>

<script>
import { defineComponent } from 'vue';
import { useStore } from '@/store/DefaultStore.js';
import { useEmojiPickerStore } from '@/store/EmojiPickerStore.js';
import EmojiPicker from './EmojiPicker.vue';

export default defineComponent({
    name: 'GlobalEmojiPicker',
    components: { EmojiPicker },

    setup() {
        const store = useStore();
        const emojiPickerStore = useEmojiPickerStore();
        return { store, emojiPickerStore };
    },

    data() {
        return {
            popupStyle: {},
        };
    },

    watch: {
        'emojiPickerStore.isOpen'(newVal) {
            if (newVal) {
                this.$nextTick(() => this.PositionPopup());
            } else {
                this.popupStyle = {};
            }
        },
    },

    mounted() {
        this._onWindowResize = () => {
            if (this.emojiPickerStore.isOpen) this.PositionPopup();
        };
        window.addEventListener('resize', this._onWindowResize);
    },

    unmounted() {
        window.removeEventListener('resize', this._onWindowResize);
    },

    methods: {
        OnSelect(emojiObj) {
            this.emojiPickerStore.Select(emojiObj);
        },

        OnClose() {
            this.emojiPickerStore.Close();
        },

        OnBackdropClick() {
            this.emojiPickerStore.Close();
        },

        PositionPopup() {
            // Mobile: bottom sheet (CSS handles it, clear inline styles).
            if (this.store.lessThan600) {
                this.popupStyle = {};
                return;
            }

            const anchor = this.emojiPickerStore.anchorRect;
            const popup = this.$refs.popup;
            if (!popup) return;

            // No anchor → center on screen.
            const popupRect = popup.getBoundingClientRect();
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            const margin = 8;
            const w = popupRect.width || 340;
            const h = popupRect.height || 400;

            if (!anchor) {
                this.popupStyle = {
                    top: `${Math.max(margin, (vh - h) / 2)}px`,
                    left: `${Math.max(margin, (vw - w) / 2)}px`,
                };
                return;
            }

            // Anchored: prefer below, flip above if no room.
            let top = anchor.bottom + 4;
            if (top + h > vh - margin) {
                top = anchor.top - h - 4;
            }
            top = Math.max(margin, Math.min(top, vh - h - margin));

            // Right-aligned to anchor, clamped to viewport.
            let left = anchor.right - w;
            left = Math.max(margin, Math.min(left, vw - w - margin));

            this.popupStyle = { top: `${top}px`, left: `${left}px` };
        },
    },
});
</script>

<style scoped>
.gep-root {
    position: fixed;
    inset: 0;
    z-index: 10000;
    pointer-events: none;
}

.gep-backdrop {
    position: absolute;
    inset: 0;
    pointer-events: auto;
    background-color: transparent;
}

.gep-backdrop--mobile {
    background-color: rgba(0, 0, 0, 0.45);
}

.gep-popup {
    position: fixed;
    pointer-events: auto;
    border-radius: 10px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
    background: white;
    overflow: hidden;
}

.gep-popup--mobile {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    top: auto;
    width: 100vw;
    max-width: 100vw;
    border-radius: 16px 16px 0 0;
    box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.2);
    animation: gep-slide-up 0.2s ease-out;
    padding-bottom: env(safe-area-inset-bottom, 0px);
}

@keyframes gep-slide-up {
    from { transform: translateY(100%); }
    to { transform: translateY(0); }
}
</style>
